import { z } from 'zod'

/**
 * Instants — the only representation of a moment in this system.
 *
 * Stored as integer epoch milliseconds UTC (`docs/ARCHITECTURE.md` §5), carried over the
 * wire as UTC ISO-8601 with a `Z` (§4). Neither form carries a local offset, and that is
 * the point: there is nothing to apply an offset to, so the current app's double-shift
 * (`docs/reference/ISSUES.md` CF5 and LF1, where `convertToUtc` re-applies the device's
 * offset to a value that already has none, shifting every recorded work time) has no
 * shape to reproduce.
 *
 * The conversions below are the *only* ones. A caller that wants to show an instant in a
 * zone formats it at display time and never converts the value it stores or sends.
 */

/** The earliest instant a JavaScript `Date` can represent. */
export const MIN_INSTANT_MS = -8_640_000_000_000_000
/** The latest instant a JavaScript `Date` can represent. */
export const MAX_INSTANT_MS = 8_640_000_000_000_000

/** An instant in integer epoch milliseconds UTC — the storage form. */
export const instantMsSchema = z.number().int().min(MIN_INSTANT_MS).max(MAX_INSTANT_MS).meta({
  id: 'InstantMs',
  description: 'An instant in integer milliseconds since the Unix epoch, UTC.',
})

/**
 * An instant as UTC ISO-8601 — the wire form.
 *
 * A trailing `Z` is required. An offset such as `+02:00` is rejected rather than
 * converted, because accepting it would mean the API silently deciding what a client's
 * ambiguous local time meant.
 */
export const utcIsoSchema = z.iso.datetime().meta({
  id: 'UtcInstant',
  description:
    'An instant as UTC ISO-8601, for example 2026-03-29T00:30:00.000Z. Offsets other than Z are rejected.',
})

/** Whether a number is an instant a `Date` can represent. */
export function isInstantMs(value: number): boolean {
  return Number.isInteger(value) && value >= MIN_INSTANT_MS && value <= MAX_INSTANT_MS
}

/**
 * An instant as its UTC ISO-8601 string. No offset is applied — there is none to apply.
 *
 * @throws if the value is not a representable instant.
 */
export function toUtcIso(instantMs: number): string {
  if (!isInstantMs(instantMs)) {
    throw new RangeError(`Not a representable instant in epoch milliseconds: ${instantMs}.`)
  }
  return new Date(instantMs).toISOString()
}

/**
 * A UTC ISO-8601 string as epoch milliseconds. The inverse of {@link toUtcIso}.
 *
 * @throws if the string is not a UTC ISO-8601 instant. An offset-bearing or zoneless
 * string is refused rather than guessed at.
 */
export function fromUtcIso(value: string): number {
  const parsed = utcIsoSchema.safeParse(value)
  if (!parsed.success) {
    throw new RangeError(`Not a UTC ISO-8601 instant: ${JSON.stringify(value)}`)
  }
  return Date.parse(parsed.data)
}
